import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  LockKeyhole
} from "lucide-react";

import "../styles/PATINFormulario.css";

const etapas = [
  {
    titulo: "Identificação",
    descricao: "Vamos começar conhecendo você e seu negócio.",
    campos: [
      ["nome_completo", "Seu nome completo", "text"],
      ["nome_negocio", "Nome do negócio ou marca", "text"],
      ["whatsapp", "WhatsApp para contato", "tel"],
      ["instagram", "Instagram ou outra rede social", "text"],
      ["area_atuacao", "Em qual área seu negócio atua?", "text"],
      ["tempo_negocio", "Há quanto tempo você empreende?", "text"],
      ["formalizacao", "Seu negócio é formalizado?", "select",
        ["Sim", "Não", "Em processo de formalização"]],
      ["equipe", "Você trabalha sozinha ou tem uma equipe?", "text"]
    ]
  },
  {
    titulo: "Raio-X do negócio",
    descricao: "Conte como seu negócio funciona hoje.",
    campos: [
      ["produtos", "Quais produtos ou serviços você oferece?", "textarea"],
      ["clientes", "Quem são suas principais clientes?", "textarea"],
      ["valor", "Por que as clientes escolhem seu negócio?", "textarea"],
      ["pontos_fortes", "Quais são os pontos fortes do seu negócio?", "textarea"],
      ["dificuldades", "Quais são suas três principais dificuldades?", "textarea"]
    ]
  },
  {
    titulo: "Diagnóstico",
    descricao: "Avalie cada afirmação de 1 a 5, pensando na realidade atual do seu negócio.",
    campos: [
      ["diag_clientes", "Conheço bem minhas clientes e suas necessidades.", "escala"],
      ["diag_oferta", "Sei explicar claramente o que meu negócio oferece.", "escala"],
      ["diag_diferencial", "Sei o que diferencia meu negócio dos concorrentes.", "escala"],
      ["diag_produtos", "Meus produtos ou serviços atendem às necessidades das clientes.", "escala"],
      ["diag_marca", "Minha marca comunica bem o que meu negócio representa.", "escala"],
      ["diag_canais", "Utilizo canais adequados para divulgar e vender.", "escala"],
      ["diag_financas", "Consigo acompanhar receitas, despesas e resultados.", "escala"],
      ["diag_metas", "Tenho objetivos claros para o crescimento do negócio.", "escala"]
    ]
  },
  {
    titulo: "Prioridades",
    descricao: "Escolha o que precisa de mais atenção neste momento.",
    campos: [
      ["areas_prioritarias", "Quais áreas precisam melhorar?", "textarea"],
      ["prioridade_1", "Primeira prioridade", "textarea"],
      ["prioridade_2", "Segunda prioridade", "textarea"],
      ["prioridade_3", "Terceira prioridade", "textarea"]
    ]
  },
  {
    titulo: "Produtos e serviços",
    descricao: "Vamos olhar para o valor que seu negócio entrega.",
    campos: [
      ["proposta_valor", "Qual é a principal proposta de valor do seu negócio?", "textarea"],
      ["principal_produto", "Qual é seu principal produto ou serviço?", "textarea"],
      ["melhorias_produtos", "O que você gostaria de melhorar na sua oferta?", "textarea"]
    ]
  },
  {
    titulo: "Marca e comunicação",
    descricao: "Pense em como as pessoas conhecem seu negócio.",
    campos: [
      ["identidade_marca", "Como você apresenta sua marca atualmente?", "textarea"],
      ["divulgacao", "Quais canais utiliza para divulgar seus produtos?", "textarea"],
      ["melhorias_comunicacao", "O que gostaria de melhorar na comunicação?", "textarea"]
    ]
  },
  {
    titulo: "Vendas e atendimento",
    descricao: "Vamos conhecer sua relação com as clientes.",
    campos: [
      ["canais_venda", "Como suas clientes compram seus produtos?", "textarea"],
      ["atendimento", "Como você organiza o atendimento?", "textarea"],
      ["whatsapp_vendas", "Como utiliza o WhatsApp para vender?", "textarea"],
      ["melhorias_vendas", "O que gostaria de melhorar nas vendas?", "textarea"]
    ]
  },
  {
    titulo: "Finanças e crescimento",
    descricao: "Organização financeira também faz parte da transformação.",
    campos: [
      ["controle_financeiro", "Como controla o dinheiro que entra e sai?", "textarea"],
      ["precificacao", "Como define os preços dos produtos ou serviços?", "textarea"],
      ["metas_crescimento", "Quais resultados gostaria de alcançar?", "textarea"]
    ]
  },
  {
    titulo: "Plano de transformação",
    descricao: "Transforme suas prioridades em ações possíveis.",
    campos: [
      ["plano_area", "Qual área será transformada?", "text"],
      ["plano_transformacao", "O que precisa ser transformado?", "textarea"],
      ["plano_acao", "O que será feito?", "textarea"],
      ["plano_como", "Como a ação será realizada?", "textarea"],
      ["plano_prazo", "Qual é o prazo previsto?", "text"],
      ["plano_resultado", "Qual resultado você espera alcançar?", "textarea"]
    ]
  }
];

function Campo({ campo, valor, atualizar }) {
  const [id, pergunta, tipo, opcoes] = campo;

  if (tipo === "escala") {
    return (
      <div className="patin-form-campo">
        <label>{pergunta}</label>

        <div className="patin-escala">
          {[1, 2, 3, 4, 5].map((nota) => (
            <button
              key={nota}
              type="button"
              className={valor === nota ? "selecionado" : ""}
              onClick={() => atualizar(id, nota)}
              aria-pressed={valor === nota}
            >
              {nota}
            </button>
          ))}
        </div>

        <div className="patin-escala-legenda">
          <span>1 = Discordo totalmente</span>
          <span>5 = Concordo totalmente</span>
        </div>
      </div>
    );
  }

  return (
    <div className="patin-form-campo">
      <label htmlFor={id}>{pergunta}</label>

      {tipo === "textarea" ? (
        <textarea
          id={id}
          value={valor || ""}
          onChange={(e) => atualizar(id, e.target.value)}
          rows={4}
          placeholder="Escreva sua resposta..."
        />
      ) : tipo === "select" ? (
        <select
          id={id}
          value={valor || ""}
          onChange={(e) => atualizar(id, e.target.value)}
        >
          <option value="">Selecione uma opção</option>
          {opcoes.map((opcao) => (
            <option key={opcao} value={opcao}>
              {opcao}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={tipo}
          value={valor || ""}
          onChange={(e) => atualizar(id, e.target.value)}
          placeholder="Digite sua resposta..."
        />
      )}
    </div>
  );
}

export default function PATINFormulario() {
  const [etapa, setEtapa] = useState(0);
  const [respostas, setRespostas] = useState({});
  const [concluido, setConcluido] = useState(false);

  const etapaAtual = etapas[etapa];
  const progresso = Math.round(
    ((etapa + 1) / etapas.length) * 100
  );

  function atualizar(id, valor) {
    setRespostas((anterior) => ({
      ...anterior,
      [id]: valor
    }));
  }

  function avancar() {
    if (etapa < etapas.length - 1) {
      setEtapa((atual) => atual + 1);
      window.scrollTo(0, 0);
    } else {
      setConcluido(true);
      window.scrollTo(0, 0);
    }
  }

  function voltar() {
    if (etapa > 0) {
      setEtapa((atual) => atual - 1);
      window.scrollTo(0, 0);
    }
  }

  return (
    <main className="patin-formulario">
      <header className="patin-form-header">
        <Link to="/patin" className="patin-form-voltar">
          <ArrowLeft size={18} />
          PATIN Digital
        </Link>

        <span>Nunca Foi Só Batom</span>
      </header>

      <div className="patin-form-container">
        {concluido ? (
          <section className="patin-form-card patin-form-concluido">
            <CheckCircle2 size={58} />

            <h1>Você chegou ao final da demonstração!</h1>

            <p>
              Você percorreu as etapas do PATIN Digital.
              Nesta versão de demonstração, suas respostas
              não foram enviadas nem salvas.
            </p>

            <Link to="/patin" className="patin-form-botao">
              Voltar ao início
            </Link>
          </section>
        ) : (
          <>
            <div className="patin-form-progresso">
              <div className="patin-form-progresso-texto">
                <span>
                  Etapa {etapa + 1} de {etapas.length}
                </span>
                <strong>{progresso}%</strong>
              </div>

              <div className="patin-form-barra">
                <div style={{ width: `${progresso}%` }} />
              </div>
            </div>

            <section className="patin-form-card">
              <span className="patin-form-etiqueta">
                MEU PLANO DE TRANSFORMAÇÃO
              </span>

              <h1>{etapaAtual.titulo}</h1>

              <p className="patin-form-descricao">
                {etapaAtual.descricao}
              </p>

              <div className="patin-form-campos">
                {etapaAtual.campos.map((campo) => (
                  <Campo
                    key={campo[0]}
                    campo={campo}
                    valor={respostas[campo[0]]}
                    atualizar={atualizar}
                  />
                ))}
              </div>

              <div className="patin-form-navegacao">
                <button
                  type="button"
                  className="patin-form-anterior"
                  onClick={voltar}
                  disabled={etapa === 0}
                >
                  <ArrowLeft size={18} />
                  Anterior
                </button>

                <button
                  type="button"
                  className="patin-form-botao"
                  onClick={avancar}
                >
                  {etapa === etapas.length - 1
                    ? "Finalizar demonstração"
                    : "Próxima etapa"}
                  <ArrowRight size={18} />
                </button>
              </div>
            </section>

            <div className="patin-form-aviso">
              <LockKeyhole size={17} />
              <span>
                Versão de demonstração. As respostas ficam
                apenas nesta tela e serão perdidas ao sair.
              </span>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
